import { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import { BillingPlan } from '@recruitee/http/types';
import { createSingleEntityStore, memoizeSelector } from '@recruitee/ngrx';

export interface Store {
  activePlan: BillingPlan;
  billingPlans: BillingPlan[];
}

const { actions, selectors, namespace, reducer } = createSingleEntityStore<Store | null>()(
  'BillingPlans',
  {
    customActions: adapter => ({
      fetchSuccess: (state, payload) => adapter.update(state, { billingPlans: payload.plans }),
      fetchActivePlan: state => adapter.markAsPending(state),
      fetchActivePlanSuccess: (state, payload: { plan: BillingPlan }) =>
        adapter.update(state, { activePlan: payload.plan }),
      fetchActivePlanFailed: (state, payload: { error: HttpErrorResponse }) =>
        adapter.setError(state, payload.error),
    }),
  },
);

const billingPlans = createSelector(selectors.data, state => (state ? state?.billingPlans : null));
const billingPlansPrices = createSelector(billingPlans, plans => {
  if (!plans?.length) return [];
  return plans.filter(plan => plan.prices.length).flatMap(plan => plan.prices);
});
const rangeForQuantity = memoizeSelector((quantity: number | undefined) =>
  createSelector(billingPlansPrices, prices => {
    if (!quantity) return '';
    const price = prices.find(price =>
      price.fteRangeEnd !== null
        ? price.fteRangeStart <= quantity && price.fteRangeEnd >= quantity
        : price.fteRangeStart <= quantity,
    );
    if (!price) throw new Error(`Invalid quantity: ${quantity}`);
    const rangeEnd = price.fteRangeEnd ?? price.fteRangeStart - 1 + 1000;
    return `${price.fteRangeStart}-${rangeEnd}`;
  }),
);

const BillingPlansSelectors = {
  ...selectors,
  activePlan: createSelector(selectors.data, state => state?.activePlan),
  billingPlans,
  rangeForQuantity,
};

export {
  actions as BillingPlansActions,
  BillingPlansSelectors,
  namespace as BillingPlansNamespace,
  reducer as BillingPlansReducer,
};
